"use client";

import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { useCallback } from "react";
import { CheckBadgeIcon, StarIcon, ClockIcon } from "@heroicons/react/24/solid";

type CategoryFiltersProps = {
  totalCompanies: number;
  neighborhoods?: string[];
};

export default function CategoryFilters({
  totalCompanies,
  neighborhoods = [],
}: CategoryFiltersProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const showVerified = searchParams.get("verified") === "true";
  const showFeatured = searchParams.get("featured") === "true";
  const showOpen = searchParams.get("open") === "true";
  const neighborhood = searchParams.get("neighborhood") || "";
  const rating = searchParams.get("rating") || "";

  const updateFilter = useCallback(
    (key: string, value: string | boolean) => {
      const params = new URLSearchParams(searchParams.toString());

      if (value === false || value === "") {
        params.delete(key);
      } else {
        params.set(key, String(value));
      }

      // Reset to page 1 when filtering
      params.delete("page");

      router.push(`${pathname}?${params.toString()}`);
    },
    [pathname, router, searchParams]
  );

  const clearFilters = useCallback(() => {
    router.push(pathname);
  }, [pathname, router]);

  const hasActiveFilters =
    showVerified || showFeatured || showOpen || neighborhood || rating;

  return (
    <aside className="hidden lg:block w-64 shrink-0">
      <div className="sticky top-24 space-y-6 px-4">
        {/* Filtros Header */}
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-lg">Filtros</h3>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-mygreen hover:underline"
            >
              Limpar
            </button>
          )}
        </div>

        {/* Resultado */}
        <div className="pb-6 border-b border-gray-200">
          <p className="text-sm text-gray-600">
            {totalCompanies} {totalCompanies === 1 ? "resultado" : "resultados"}
          </p>
        </div>

        {/* Filtros de Status */}
        <div className="space-y-4 pb-6 border-b border-gray-200">
          <h4 className="font-medium text-sm text-gray-900">Status</h4>

          <label className="flex items-center gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={showVerified}
              onChange={(e) => updateFilter("verified", e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-mygreen focus:ring-mygreen cursor-pointer"
            />
            <div className="flex items-center gap-2">
              <CheckBadgeIcon className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-gray-700 group-hover:text-gray-900">
                Empresas Verificadas
              </span>
            </div>
          </label>

          <label className="flex items-center gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={showFeatured}
              onChange={(e) => updateFilter("featured", e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-mygreen focus:ring-mygreen cursor-pointer"
            />
            <div className="flex items-center gap-2">
              <StarIcon className="w-4 h-4 text-yellow-500" />
              <span className="text-sm text-gray-700 group-hover:text-gray-900">
                Empresas em Destaque
              </span>
            </div>
          </label>

          <label className="flex items-center gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={showOpen}
              onChange={(e) => updateFilter("open", e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-mygreen focus:ring-mygreen cursor-pointer"
            />
            <div className="flex items-center gap-2">
              <ClockIcon className="w-4 h-4 text-green-600" />
              <span className="text-sm text-gray-700 group-hover:text-gray-900">
                Abertas Agora
              </span>
            </div>
          </label>
        </div>

        {/* Filtros de Localização */}
        <div className="space-y-4 pb-6 border-b border-gray-200">
          <h4 className="font-medium text-sm text-gray-900">Localização</h4>

          <select
            value={neighborhood}
            onChange={(e) => updateFilter("neighborhood", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-mygreen focus:border-transparent"
          >
            <option value="">Todos os bairros</option>
            {neighborhoods.map((n) => (
              <option key={n} value={n.toLowerCase().replace(/\s+/g, "-")}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>
    </aside>
  );
}
